/* eslint-disable import/no-cycle */
import * as signalR from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import { useSnackbar } from "notistack";
import { Profile } from "oidc-client";
import React, { createContext, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route } from "react-router-dom";
import { Vector3 } from "three";
import {
    GetAssetModel,
    GetAssetsForDbidContentModel,
    IndexModel,
    ProjectDto,
    RenderModel2,
    UserAlarmsDto,
    UserNotificationDto,
    UserWithPermits,
} from "../api/APIClient";
import {
    fetchGetCountEquipos,
    fetchPermitsToWork,
    fetchPowerBIReports,
    getRenderConfig,
    updateRenderConfig,
} from "../api/Fetchers";
import { IPowerBIReportElementAPI } from "../api/Interfaces";
import {
    alarmsService,
    chatMessageService,
    documentationService,
    notificationsService,
    projectsService,
    renderModelService,
    userInformationService,
} from "../api/Services";
import { authService } from "../auth/AuthorizeService";
import { usePureEffect } from "../helpers/usePureEffect";
import { RenderModel, RenderModelType, RightPanelsModel } from "../Models/AssetInformationModels";
import { ChartsModel } from "../Models/SignalModels";
import { Roles, User } from "../Models/UserTable.model";
import { ProjectContext } from "./Project/Context/ProjectContext";
import { ViewerMode } from "./Project/Viewer/ForgeViewer/BIMDigitalTwinViewer/Utils";
import { BlindFlangePanels } from "../NewModels/BlindFlanges/Models";
import { RenderConfigInput } from "./Project/Settings/Subscriptions/Models";
import { PrototypeViewPanels, RenderItem } from "./Project/Settings/ViewsControl/Models";
import { GetTaskStatusLegend } from "../NewModels/Planning/External/Models";

const userPermitsConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/userPermits", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

const renderConfigurationConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/renderConfiguration", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

interface IPanelViewerProviderProps {
    children: React.ReactNode;
}
export interface UserRole {
    userId: string;
    userName: string;
    roleId: string;
    roles: string;
    projectId: string;
    sections: ModulesInterface;
}
export interface ModulesInterface {
    sectionId: string;
    sectionName: string;
    modules: unknown;
}

interface IObjTest {
    xcoord: number;
    ycoord: number;
    data: ChartsModel;
}

interface IAlarmsHeatMap {
    dbIds: number[] | undefined;
    color: string | undefined;
}

interface GanttColumns {
    columnHeader: string;
    values: string;
}

interface ILegendObj {
    value: string;
    color: string;
    show: boolean;
    tag: string;
}

let defaulValues;

const PanelProjectContext = createContext(defaulValues);

const PanelProjectProvider = ({ children }: IPanelViewerProviderProps): JSX.Element => {
    const { t } = useTranslation("project");
    const { enqueueSnackbar } = useSnackbar();

    const [project, setProject] = useState<ProjectDto>(
        React.useContext<ProjectDto>(ProjectContext)
    );
    const [prototypeViewLegends, setPrototypeViewLegends] = useState<
        GetTaskStatusLegend[] | undefined
    >(undefined);
    const [notifications, setNotifications] = useState<UserNotificationDto[]>([]);
    const [alarms, setAlarms] = useState<UserAlarmsDto[]>([]);
    const [userProfile, setUserProfile] = useState<Profile | null>(null);
    const [userRole, setUserRole] = useState<UserRole>();
    const [userPermits, setUserPermits] = useState<UserWithPermits>();
    const [isUserPermitsLoading, setIsUserPermitsLoading] = useState<boolean>(true);
    const [errorInUserPemirts, setErrorInUserPemirts] = useState<boolean>(false);
    const [redirectCommentsPanel, setRedirectCommentsPanel] = useState<boolean>(false);
    const [isViewerReady, setisViewerReady] = useState<boolean>(false);
    const [view, setView] = React.useState("");
    const [filterView, setFilterView] = React.useState("");
    const [hoveredDeviceInfo, setHoveredDeviceInfo] = useState<IObjTest | undefined>(undefined);
    const [rightPanelsData, setRightPanelsData] = useState<RightPanelsModel | undefined>({});
    const [isRightPanelOpen, setIsRightPanelOpen] = useState<boolean>(false);
    const [isViewerLoading, setIsViewerLoading] = useState<boolean>(false);
    const [isRenderingItems, setIsRenderingItems] = useState<boolean>(false);
    const [assetSelection, setAssetSelection] = useState<IndexModel | undefined>({});
    const [viewerMode, setViewerMode] = useState<ViewerMode>(ViewerMode.AssetInfo);
    const [dbidsToRender, setDbidsToRender] = useState<number[]>([]);
    const [renderModel, setRenderModel] = useState<RenderModel2[] | undefined>(undefined);
    const [dbidsAlarmsHeatMap, setDbidsAlarmsHeatMap] = useState<IAlarmsHeatMap[]>([]);
    const [userId, setUserId] = useState<string>("");
    // Admin management
    const [editing, setEditing] = React.useState(false);
    const [changePermitsTables, setChangePermitsTables] = React.useState(true);
    const [submitEditChanges, setSubmitEditChanges] = React.useState(false);
    const [tableFrom, setTableFrom] = React.useState<string>("");
    const [showSave, setShowSave] = React.useState<boolean>(false);
    const [openDialogRole, setOpenDialogRole] = useState("");
    const [openDialogEditUser, setOpenDialogEditUser] = useState("");
    const [showDialogEditRole, setShowDialogEditRole] = React.useState<string>("");
    const [showDeleteDialog, setShowDeleteDialog] = useState("");
    const [openDialogUsersAssociates, setOpenDialogUsersAssociates] = useState<string>("");

    // Users table
    const [cellValues, setCellValues] = React.useState<User[]>([]);
    const [users, setUsers] = React.useState<User[]>([]);
    // Roles table
    const [cellValuesRoles, setCellValuesRoles] = React.useState<Roles[]>([]);
    const [roles, setRoles] = React.useState<Roles[]>([]);
    // Roles By Users Table
    const [submitEditChangesRoles, setSubmitEditChangesRoles] = React.useState(false);
    const [userProfileId, setUserProfileId] = useState<string>();
    const [userProfilePhoto, setUserProfilePhoto] = useState<string | undefined>(undefined);

    const [assetType, setAssetType] = useState<string | undefined>(undefined);
    const [assetEditModel, setAssetEditModel] = useState<GetAssetModel | undefined>(undefined);
    const [parent, setParent] = React.useState<string | undefined>(undefined);
    const [dbIdsToEdit, setDbIdsToEdit] = useState<number[] | undefined>(undefined);
    const [assetIdEdit, setAssetIdEdit] = useState<string | undefined>(undefined);
    const [openEditAsset, setOpenEditAsset] = React.useState(false);
    const [assetsWithSameDbid, setAssetsWithSameDbid] = useState<
        GetAssetsForDbidContentModel[] | undefined
    >(undefined);
    const [renderModelTypes, setRenderModelTypes] = useState<RenderModelType[]>([]);
    // const [blobStorageSelected, setBlobStorageSelected] = useState<string>("azure");
    const [powerBIFilteredData, setPowerBIFilteredData] = useState<IPowerBIReportElementAPI[]>([]);
    const [powerBILegend, setPowerBILegend] = useState<string[]>([]);
    const [powerBIFilter, setPowerBIFilter] = useState<string>("");

    const [projectsCount, setProjectsCount] = useState<number>(0);
    const [documentsCount, setDocumentsCount] = React.useState<number>(0);
    const [teamsCount, setTeamsCount] = React.useState<number>(0);
    const [workOrdersCount, setWorkOrdersCount] = React.useState<number | undefined>(0);
    const [usersWithAccessCount, setUsersWithAccessCount] = React.useState<number>(0);
    const [powerBIReportsCount, setPowerBIReportsCount] = React.useState<number>(0);
    const [chatSubscriptionsCount, setChatSubscriptionsCount] = React.useState<number>(0);
    const [signalSubscriptionsCount, setSignalSubscriptionsCount] = React.useState<number>(0);
    const [notificationsCount, setNotificationsCount] = React.useState<number>(0);
    const [alarmsCount, setAlarmsCount] = React.useState<number>(0);
    const [workOrdersOpen, setWorkOrdersOpen] = React.useState<string[]>([]);
    const [interferenceOrdersOpen, setInterferenceOrdersOpen] = React.useState<string[]>([]);

    const [rightPanelButtonsLoading, setRightPanelButtonsLoading] = useState<boolean>(false);
    // gantt viewer
    const [foreingCellsValues, setForeingCellsValues] = useState<GanttColumns[]>([]);
    const [foreingHeaderValues, setForeingHeaderValues] =
        useState<{ columnHeader: string; values: string }>();
    const [clickCoords, setClickCoords] = useState<Vector3 | undefined>(undefined);
    const [coordinatesMode, setCoordinatesMode] = useState(false);

    // subpanels & assets related
    const [isSubpanelLoading, setIsSubpanelLoading] = useState<boolean>(false);
    const [focusedAssetId, setFocusedAssetId] = useState<string>("");

    const [legendObj, setLegendObj] = React.useState<ILegendObj[]>([]);
    const [renderModelApi, setRenderModelApi] = React.useState<RenderModel[]>([]);
    const [renderModelToPaint, setRenderModelToPaint] = React.useState<RenderModel[]>([]);

    // Blind Flanges
    const [blindFlangeRightPanelData, setBlindFlangeRightPanelData] =
        useState<BlindFlangePanels | null>(null);
    const [isBlindFlangeRightPanelOpen, setIsBlindFlangeRightPanelOpen] = useState<boolean>(false);
    const [isBFRightPanelButtonsLoading, setIsBFRightPanelButtonsLoading] =
        useState<boolean>(false);

    // Prototype Views
    const [prototypeViewRightPanelData, setPrototypeViewRightPanelData] =
        useState<PrototypeViewPanels | null>(null);
    const [isPrototypeViewRightPanelOpen, setIsPrototypeViewRightPanelOpen] =
        useState<boolean>(false);
    const [isPVRightPanelButtonsLoading, setIsPVRightPanelButtonsLoading] =
        useState<boolean>(false);
    const [prototypeViewRenderData, setPrototypeViewRenderData] = useState<RenderItem[]>([]);

    // small volumes
    const [smallVolumesConfig, setSmallVolumesConfig] = React.useState<
        RenderConfigInput | undefined
    >({
        projectId: project.id,
        input: {
            projectId: project.id,
            minSize: 0,
            sphereRadius: 0,
        },
    });
    const [smallVolumesSwitch, setSmallVolumesSwitch] = React.useState<boolean>(false);
    const [smallVolummesTrigger, setSmallVolumesTrigger] = React.useState<boolean>(false);
    const [isSmallVolumesSwitchAvailable, setIsSmallVolumesSwitchAvailable] =
        React.useState<boolean>(true);

    /**
     * Junta en una sola etiqueta todas las que caen sobre el mismo destino: el mismo
     * activo (mismos dbIds) o la misma coordenada.
     *
     * Hacía falta porque addLabelHeatMap construye el id del elemento HTML a partir del
     * destino y, antes de crear la etiqueta nueva, borra la que encuentre con ese id. Al
     * llamarla siempre con `id: undefined` todas las etiquetas del mismo activo comparten
     * id, así que cada una borraba a la anterior y solo sobrevivía la última.
     *
     * La etiqueta agrupada lleva "N etiquetas" como cabecera y el contenido de cada una
     * en `tooltip.data`, que addLabelHeatMap convierte en una lista oculta que se abre y
     * cierra al hacer clic. Un destino con una sola etiqueta sale igual que antes.
     *
     * El contenido va tal cual, con su HTML. Antes se limpiaba a texto plano porque
     * addLabelHeatMap rellenaba cada elemento de la lista con textContent, y eso dejaba
     * sin imagen ni enlace a los tooltips que sí se veían cuando no compartían destino.
     * Ahora se le pasa `infoAsHtml` y pinta el HTML igual que en la etiqueta suelta.
     */
    const groupTooltipsByTarget = (renderInformation: RenderModel[]): RenderModel[] => {
        const targetKey = (e: RenderModel): string | null => {
            if (e.dbIds && e.dbIds.length > 0) return `dbIds:${e.dbIds.join("-")}`;
            if (e.coords) return `coords:${e.coords.x}|${e.coords.y}|${e.coords.z}`;
            return null;
        };

        // Map mantiene el orden de inserción: los destinos salen en el orden en que
        // aparecieron por primera vez.
        const byTarget = new Map<string, RenderModel[]>();
        const withoutTarget: RenderModel[] = [];

        renderInformation.forEach((e) => {
            const key = targetKey(e);
            if (key === null) {
                withoutTarget.push(e);
                return;
            }
            const bucket = byTarget.get(key);
            if (bucket) bucket.push(e);
            else byTarget.set(key, [e]);
        });

        const grouped: RenderModel[] = [];

        byTarget.forEach((bucket) => {
            if (bucket.length === 1) {
                grouped.push(bucket[0]);
                return;
            }

            const data: { [key: string]: string } = {};
            bucket.forEach((e, i) => {
                data[`${i + 1}`] = e.tooltip?.content ?? "";
            });

            grouped.push({
                ...bucket[0],
                tooltip: {
                    ...(bucket[0].tooltip ?? {}),
                    type: "html",
                    content: `<center>${bucket.length} etiquetas</center>`,
                    data,
                },
            });
        });

        return [...grouped, ...withoutTarget];
    };

    const renderHTMLTooltips = (renderInformation: RenderModel[]) => {
        if (
            renderInformation.every(
                (d) =>
                    (d.dbIds !== null && d.dbIds !== undefined) ||
                    ((d.coords !== null || d.coords !== undefined) &&
                        d.coords?.x &&
                        d.coords?.y &&
                        d.coords?.z)
            )
        ) {
            if (
                renderInformation.every((e) => e.type === RenderModelType.Parent) &&
                viewerMode !== ViewerMode.PrototypeViews
            ) {
                enqueueSnackbar(`${t("viewer.messages.showOnlyParents")}`, {
                    variant: "info",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
            }
            setIsRenderingItems(true);
            window.NOP_VIEWER?.clearAllLabels();
            groupTooltipsByTarget(renderInformation ?? []).forEach((e) => {
                // El ultimo true es infoAsHtml: el contenido de la lista viene del Excel
                // y es HTML, no pares clave/valor. Solo lo pasan estas dos llamadas.
                if (e.dbIds !== null && e.dbIds !== undefined && e.dbIds.length > 0) {
                    window.NOP_VIEWER?.addLabelHeatMap(
                        e.dbIds!,
                        e.tooltip,
                        e.style?.hexColor,
                        e.tooltip?.data,
                        true,
                        undefined,
                        undefined,
                        true
                    );
                } else {
                    window.NOP_VIEWER?.addLabelHeatMap(
                        [],
                        e.tooltip,
                        e.style?.hexColor,
                        e.tooltip?.data,
                        true,
                        undefined,
                        e.coords,
                        true
                    );
                }
            });
            if (window.NOP_VIEWER?.showLabels) {
                window.NOP_VIEWER?.showAllLabels();
            } else {
                window.NOP_VIEWER?.hideAllLabels();
            }
        }
    };

    useEffect(() => {
        if (!window.NOP_VIEWER) return;
        if (prototypeViewRenderData.length > 0) {
            console.log("===> Renderizando vista prototipo", prototypeViewRenderData);
            let itemsCountError = 0;
            const allToolTips = prototypeViewRenderData
                ?.filter((renderItem) => renderItem.tooltip?.content !== null) // Filtra elementos cuyo tooltip.content no sea null
                .map((renderItem) => {
                    // Usa const para variables que no se reasignan
                    const { id: id2, geometry: geometry2, ...toolTipInformation } = renderItem;

                    // Usa let porque se reasigna toolTipInformation
                    let updatedToolTipInformation = toolTipInformation;

                    // Agregar la propiedad coords si dbIds no existe o es un array vacío
                    if (!toolTipInformation.dbIds || toolTipInformation.dbIds.length === 0) {
                        // @TODO: quitar este trycatch cuando API lo arregle
                        try {
                            // @ts-expect-error: Ignorar error de tipos para dbIds y coords
                            updatedToolTipInformation = {
                                ...toolTipInformation,
                                dbIds: toolTipInformation.dbIds || [], // Asegura que dbIds siempre sea un array
                                coords: renderItem.geometry[0].center, // Agrega coords
                            } as RenderModel;
                        } catch (error) {
                            console.log("===> Error al agregar coords:", error);
                            itemsCountError = 1 + itemsCountError;
                        }
                    }

                    return updatedToolTipInformation;
                })
                .flat() as RenderModel[];

            renderHTMLTooltips(allToolTips);
            window.NOP_VIEWER.dbidsToRender =
                prototypeViewRenderData?.map((renderItem) => renderItem.dbIds).flat() || [];
            setIsRenderingItems(true);

            if (itemsCountError > 0) {
                // @TODO: traducción
                enqueueSnackbar(
                    `Algunos elementos no se han podido renderizar: ${itemsCountError}`,
                    {
                        variant: "error",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    }
                );
            }
        } else {
            renderHTMLTooltips([]);
        }

        prototypeViewRenderData.forEach((elementToRender) => {
            // si hay dbIds, se pinta el activo
            if (elementToRender.dbIds.length > 0) {
                window.NOP_VIEWER?.prototypeViewColor(
                    `${elementToRender.id}`,
                    elementToRender.style.hexColor,
                    0.5,
                    elementToRender.dbIds
                );
                window.NOP_VIEWER?.showDisks(
                    [`${elementToRender.id}`],
                    [
                        {
                            id: `${elementToRender.id}`,
                            tag: `${elementToRender.id}`,
                        },
                    ]
                );
            }
            if (elementToRender.geometry) {
                elementToRender.geometry.forEach((e, i) => {
                    // cada geometria necesita su propio tag: nombra la escena de overlay,
                    // asi que si se repite las geometrias del mismo elemento se pisan
                    const geometryTag = `${elementToRender.id}_${i}`;
                    // si tiene volumen, no tiene center, no tiene urn, es una geometria asociada a un activo
                    // si tiene volumen, si tiene center, no tiene urn, es una geometria sencilla asociada a una coodenada
                    // no tiene volumen, si tiene center, si tiene urn, es una geometria compleja como por ejemplo una grua
                    if (e.volume && !e.center && !e.urn) {
                        // es una geometria asosiada a un activo
                        window.NOP_VIEWER?.prototypeViewVolume(
                            geometryTag,
                            e.volume?.radius as number,
                            elementToRender.style.hexColor,
                            0.5,
                            e.volume?.shape as string,
                            e.volume?.height as number,
                            elementToRender.dbIds
                        );
                        window.NOP_VIEWER?.showDisks(
                            [geometryTag],
                            [
                                {
                                    id: geometryTag,
                                    tag: geometryTag,
                                },
                            ]
                        );
                    }
                    if (e.volume && e.center && !e.urn) {
                        // es una geometria sencilla
                        window.NOP_VIEWER?.prototypeViewVolume(
                            geometryTag,
                            e.volume?.radius as number,
                            elementToRender.style.hexColor,
                            0.5,
                            e.volume?.shape as string,
                            e.volume?.height as number,
                            undefined,
                            e.center
                        );
                        window.NOP_VIEWER?.showDisks(
                            [geometryTag],
                            [
                                {
                                    id: geometryTag,
                                    tag: geometryTag,
                                },
                            ]
                        );
                    }
                    // if((!e.volume && e.center && e.urn)){
                    //     // es una geometria compleja
                    //     return
                    // }
                });
            }
        });

        window.NOP_VIEWER.isolated = false;
        window.NOP_VIEWER?.isolate();
        window.NOP_VIEWER?.setGhosting(false);
        if (window.NOP_VIEWER?.showLabels) window.NOP_VIEWER?.showAllLabels();
    }, [prototypeViewRenderData]);

    const UserPermitsCall = () => {
        if (project.id && userId) {
            userInformationService
                .getUserWithPermits({ projectId: project.id, userId })
                .then((response) => {
                    setUserPermits(response.data);
                    setIsUserPermitsLoading(false);
                    setErrorInUserPemirts(false);
                })
                .catch((err) => {
                    setErrorInUserPemirts(true);
                });
        }
    };

    const renderConfigCall = async () => {
        if (project.id && userId && smallVolumesConfig) {
            const updatedConfig = {
                projectId: smallVolumesConfig.projectId,
                input: {
                    projectId: smallVolumesConfig.projectId,
                    minSize: smallVolumesConfig.input.minSize / 1000,
                    sphereRadius: smallVolumesConfig.input.sphereRadius / 1000,
                },
            };
            try {
                await updateRenderConfig(updatedConfig);
                await renderModelService.refreshRender();
                if (viewerMode !== ViewerMode.BlindFlanges) {
                    enqueueSnackbar(t("viewer.settings.smallVolumesUpdated"), {
                        variant: "success",
                    });
                }
                try {
                    const res = await getRenderConfig({ projectId: project.id });
                    setSmallVolumesConfig({
                        projectId: project.id,
                        input: {
                            projectId: project.id,
                            minSize: res.data.data.minSize,
                            sphereRadius: res.data.data.sphereRadius,
                        },
                    });
                } catch (err) {
                    enqueueSnackbar(t("viewer.settings.errorObtainingSmallVolumnes"), {
                        variant: "error",
                    });
                }
            } catch (err) {
                enqueueSnackbar(t("viewer.settings.errorUpdatingSmallVolumnes"), {
                    variant: "error",
                });
            }
        }
    };

    const isAuth = (action: string, module: string): boolean => {
        if (userPermits?.sections) {
            const sectionFound = userPermits.sections[module];
            if (sectionFound?.modules) {
                const moduleFound = sectionFound.modules[action];
                if (moduleFound) {
                    return true;
                }
            }
        }
        return false;
    };

    const isUserAllowedById = (id: string): boolean => {
        if (userId === id) {
            return true;
        }
        return false;
    };
    const createRouteBasedOnAuth = (
        isAuthorized: boolean,
        authPath: string,
        AuthComponent: React.ComponentType,
        routeToRedirect: string
    ) => {
        return (
            <Route
                path={authPath}
                render={() => {
                    return isAuthorized ? (
                        <AuthComponent />
                    ) : (
                        <>
                            <Redirect to={routeToRedirect} />
                        </>
                    );
                }}
            />
        );
    };

    const subscribeToUserPermitsSignalR = async () => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                userPermitsConnection.start().then(() => {
                    authService.getProfile().then((prof) => {
                        if (prof !== null) {
                            userPermitsConnection.invoke("AddUserPermitsConnection", prof.oid);
                            userPermitsConnection.on("PushPermitsUpdated", () => {
                                UserPermitsCall();
                            });
                        }
                    });
                });
            }
        });
    };
    const subscribeTorenderConfigurationSignalR = async () => {
        renderConfigurationConnection.start().then(() => {
            authService.getProfile().then((prof) => {
                if (prof !== null) {
                    renderConfigurationConnection.invoke(
                        "AddRenderConfigurationConnection",
                        prof.oid
                    );
                    renderConfigurationConnection.on("PushRenderToUser", () => {
                        getRenderConfig({ projectId: project.id })
                            .then((res) => {
                                setSmallVolumesConfig({
                                    projectId: project.id,
                                    input: {
                                        projectId: project.id,
                                        minSize: res.data.data.minSize,
                                        sphereRadius: res.data.data.sphereRadius,
                                    },
                                });
                            })
                            .catch(() => {
                                enqueueSnackbar(t("viewer.settings.errorObtainingSmallVolumnes"), {
                                    variant: "error",
                                });
                            });
                    });
                }
            });
        });
    };

    React.useEffect(() => {
        notificationsService.getUnreadNotifications(project.id).then((resp) => {
            if (resp) {
                setNotifications(resp.data);
            } else {
                setNotifications([]);
            }
        });
        notificationsService.getUnreadNotificationsCount(project.id).then((resp) => {
            if (resp) {
                setNotificationsCount(resp.data.number);
            } else {
                setNotificationsCount(0);
            }
        });

        alarmsService.getUnreadAlarms({ projectId: project.id }).then((resp) => {
            if (resp) {
                setAlarms(resp.data);
            } else {
                setAlarms([]);
            }
        });
        alarmsService.getUnreadAlarmsCount({ projectId: project.id }).then((resp) => {
            if (resp) {
                setAlarmsCount(resp.data.number);
            } else {
                setAlarmsCount(0);
            }
        });

        documentationService.getDocumentsFromAzure(project.id).then((res) => {
            setDocumentsCount(res.data.documents?.length ?? 0);
        });
        projectsService.getAllProjectsCount().then((res) => {
            setProjectsCount(res.data.number ?? 0);
        });

        if (project.id !== undefined) {
            chatMessageService
                .getChatMessageSubscriptionsByUser({ projectId: project.id })
                .then((res) => {
                    setChatSubscriptionsCount(res.data.subscriptions?.length ?? 0);
                });
            alarmsService.getAlarms({ projectId: project.id }).then((res) => {
                setSignalSubscriptionsCount(res.data.alarms?.length ?? 0);
            });
            fetchPowerBIReports(project.id).then((res) => {
                setPowerBIReportsCount(res.data.length);
            });
            fetchPermitsToWork(project.id, undefined).then((res) => {
                setWorkOrdersCount(res.data.permitsToWork?.permitsToWork?.length);
            });
            fetchGetCountEquipos({ projectId: project.id }).then((res) => {
                setTeamsCount(res.data.count);
            });
        }

        authService.getProfile().then((resp) => {
            if (resp) {
                setUserId(resp.oid);
            }
        });
    }, [project]);

    const handleCoordinatesClick = useCallback((ev) => {
        const result = window.NOP_VIEWER?.clientToWorld(ev.offsetX, ev.offsetY);
        if (result && result.point) {
            setClickCoords(result.point);
            window.NOP_VIEWER?.deleteAllSimpleVolumes();
            window.NOP_VIEWER?.addSimpleSphere(
                {
                    volume: {
                        shape: "sphere",
                        height: 0,
                        radius: 500,
                    },
                    urn: "",
                    center: {
                        x: result.point.x,
                        y: result.point.y,
                        z: result.point.z,
                    },
                    color: "#FF0000",
                },
                1
            );
        }
    }, []);

    useEffect(() => {
        if (coordinatesMode) {
            document
                .getElementsByClassName("adsk-viewing-viewer")[0]
                ?.addEventListener("click", handleCoordinatesClick);
        } else {
            document
                .getElementsByClassName("adsk-viewing-viewer")[0]
                ?.removeEventListener("click", handleCoordinatesClick);
        }
    }, [coordinatesMode, handleCoordinatesClick]);

    useEffect(() => {
        UserPermitsCall();
    }, [project, userId]);
    useEffect(() => {
        renderConfigCall();
    }, [smallVolummesTrigger]);
    useEffect(() => {
        if (smallVolumesSwitch) {
            renderConfigCall();
        }
    }, [smallVolumesSwitch]);

    usePureEffect(() => {
        userInformationService.setUserInformation({}).then((resp) => {
            setUserProfileId(resp.data.userId);
            setUserProfilePhoto(resp.data.photo);
        });

        alarmsService.getUsersFromAzure({ projectId: project.id }).then((res) => {
            setUsersWithAccessCount(res.data.length);
        });

        if (userPermitsConnection.state === HubConnectionState.Connected) {
            userPermitsConnection.stop().then(() => {
                subscribeToUserPermitsSignalR();
            });
        } else {
            subscribeToUserPermitsSignalR();
        }

        if (renderConfigurationConnection.state === HubConnectionState.Connected) {
            renderConfigurationConnection.stop().then(() => {
                subscribeTorenderConfigurationSignalR();
            });
        } else {
            subscribeTorenderConfigurationSignalR();
        }
        getRenderConfig({ projectId: project.id })
            .then((res) => {
                setSmallVolumesConfig({
                    projectId: project.id,
                    input: {
                        projectId: project.id,
                        minSize: res.data.data.minSize,
                        sphereRadius: res.data.data.sphereRadius,
                    },
                });
            })
            .catch(() => {
                enqueueSnackbar(t("viewer.settings.errorObtainingSmallVolumnes"), {
                    variant: "error",
                });
            });
    }, [project]);

    return (
        <PanelProjectContext.Provider
            value={{
                project,
                setProject,
                notifications,
                setNotifications,
                notificationsCount,
                setNotificationsCount,
                alarms,
                setAlarms,
                alarmsCount,
                setAlarmsCount,
                redirectCommentsPanel,
                setRedirectCommentsPanel,
                isViewerReady,
                setisViewerReady,
                view,
                setView,
                setDbidsToRender,
                dbidsToRender,
                hoveredDeviceInfo,
                setHoveredDeviceInfo,
                rightPanelsData,
                setRightPanelsData,
                isRightPanelOpen,
                setIsRightPanelOpen,
                isViewerLoading,
                setIsViewerLoading,
                isRenderingItems,
                setIsRenderingItems,
                assetSelection,
                setAssetSelection,
                viewerMode,
                setViewerMode,
                dbidsAlarmsHeatMap,
                setDbidsAlarmsHeatMap,
                renderModel,
                setRenderModel,
                userProfile,
                setUserProfile,
                userRole,
                setUserRole,
                userPermits,
                setUserPermits,
                cellValues,
                workOrdersOpen,
                setWorkOrdersOpen,
                setCellValues,
                editing,
                setEditing,
                changePermitsTables,
                setChangePermitsTables,
                submitEditChanges,
                setSubmitEditChanges,
                users,
                setUsers,
                cellValuesRoles,
                setCellValuesRoles,
                roles,
                setRoles,
                tableFrom,
                setTableFrom,
                showSave,
                setShowSave,
                submitEditChangesRoles,
                setSubmitEditChangesRoles,
                assetType,
                setAssetType,
                assetEditModel,
                setAssetEditModel,
                parent,
                setParent,
                dbIdsToEdit,
                setDbIdsToEdit,
                assetIdEdit,
                setAssetIdEdit,
                openEditAsset,
                setOpenEditAsset,
                assetsWithSameDbid,
                setAssetsWithSameDbid,
                userId,
                setUserId,
                filterView,
                setFilterView,
                renderModelTypes,
                setRenderModelTypes,
                userProfileId,
                userProfilePhoto,
                powerBIFilteredData,
                setPowerBIFilteredData,
                powerBILegend,
                setPowerBILegend,
                powerBIFilter,
                setPowerBIFilter,
                openDialogEditUser,
                setOpenDialogEditUser,
                openDialogRole,
                setOpenDialogRole,
                projectsCount,
                setProjectsCount,
                documentsCount,
                setDocumentsCount,
                teamsCount,
                setTeamsCount,
                workOrdersCount,
                setWorkOrdersCount,
                usersWithAccessCount,
                setUsersWithAccessCount,
                powerBIReportsCount,
                setPowerBIReportsCount,
                chatSubscriptionsCount,
                setChatSubscriptionsCount,
                signalSubscriptionsCount,
                setSignalSubscriptionsCount,
                rightPanelButtonsLoading,
                setRightPanelButtonsLoading,
                interferenceOrdersOpen,
                setInterferenceOrdersOpen,
                isAuth,
                isUserAllowedById,
                createRouteBasedOnAuth,
                isUserPermitsLoading,
                errorInUserPemirts,
                openDialogUsersAssociates,
                setOpenDialogUsersAssociates,
                showDialogEditRole,
                setShowDialogEditRole,
                showDeleteDialog,
                setShowDeleteDialog,
                foreingCellsValues,
                setForeingCellsValues,
                foreingHeaderValues,
                setForeingHeaderValues,
                clickCoords,
                coordinatesMode,
                setCoordinatesMode,
                isSubpanelLoading,
                setIsSubpanelLoading,
                focusedAssetId,
                setFocusedAssetId,
                legendObj,
                setLegendObj,
                renderModelApi,
                setRenderModelApi,
                renderModelToPaint,
                setRenderModelToPaint,
                blindFlangeRightPanelData,
                setBlindFlangeRightPanelData,
                isBlindFlangeRightPanelOpen,
                setIsBlindFlangeRightPanelOpen,
                isBFRightPanelButtonsLoading,
                setIsBFRightPanelButtonsLoading,
                smallVolumesConfig,
                setSmallVolumesConfig,
                smallVolummesTrigger,
                setSmallVolumesTrigger,
                smallVolumesSwitch,
                setSmallVolumesSwitch,
                isSmallVolumesSwitchAvailable,
                setIsSmallVolumesSwitchAvailable,
                prototypeViewRightPanelData,
                setPrototypeViewRightPanelData,
                isPrototypeViewRightPanelOpen,
                setIsPrototypeViewRightPanelOpen,
                isPVRightPanelButtonsLoading,
                setIsPVRightPanelButtonsLoading,
                prototypeViewRenderData,
                setPrototypeViewRenderData,
                prototypeViewLegends,
                setPrototypeViewLegends,
            }}
        >
            {children}
        </PanelProjectContext.Provider>
    );
};

export { PanelProjectProvider };

export default PanelProjectContext;
